import { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import './User.css';

const UserDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, permissions, token } = useAuth();
    const hasEditAdministrationPermission = permissions.includes('Edit Administration');

    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [isSavingClaims, setIsSavingClaims] = useState(false);
    const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [isClaimEditMode, setIsClaimEditMode] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        FirstName: '', LastName: '', UserName: '', Email: '', ContactNumber: '', Roles: [],
    });
    const [formDataClaim, setFormDataClaim] = useState({
        FirstName: '', LastName: '', UserName: '', Email: '', ContactNumber: '', Claim: [],
    });

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, token]);

    useEffect(() => {
        fetchData('UserApi', `/UserEdit/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const { User } = response.response_data;
                    setFormData((prev) => ({
                        ...prev, ...User,
                        Roles: response.response_data.Roles ? response.response_data.Roles.map((r) => ({ ...r })) : [],
                    }));
                    setFormDataClaim((prev) => ({
                        ...prev, ...User,
                        Claim: response.response_data.Claims ? response.response_data.Claims.map((c) => ({ ...c })) : [],
                    }));
                }
            })
            .catch((e) => console.error('Error fetching User Details:', e));
    }, [id]);

    const getClaimRange = () => {
        const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
        return { startIndex, endIndex: startIndex + 4 };
    };

    const handleChangeRole = (index) => {
        const updatedRoles = formData.Roles.map((role, i) => ({ ...role, IsSelected: i === index ? !role.IsSelected : false }));
        setFormData((prev) => ({ ...prev, Roles: updatedRoles }));
    };

    const handleChangeClaim = (index) => {
        const updatedClaims = [...formDataClaim.Claim];
        updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
        setFormDataClaim((prev) => ({ ...prev, Claim: updatedClaims }));
    };

    const saveRoles = async () => {
        try {
            setIsSavingRoles(true);
            const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, formData, token);
            if (response.responseCode === '1111') setSuccessMessage('Roles saved successfully');
            setIsEditMode(false);
            history.push(`/userDetail/${id}`);
        } catch (e) { console.error('Error saving roles:', e); }
        finally { setIsSavingRoles(false); }
    };

    const saveClaims = async () => {
        try {
            setIsSavingClaims(true);
            const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, formDataClaim, token);
            if (response.responseCode === '1111') setSuccessMessage('Claims saved successfully');
            setIsClaimEditMode(false);
            history.push(`/userDetail/${id}`);
        } catch (e) { console.error('Error saving claims:', e); }
        finally { setIsSavingClaims(false); }
    };

    return (
        <div className="user-container">
            <div className="user-header-card">
                <div className="user-header-content">
                    <div className="user-title-section">
                        <span className="user-title-icon"><PeopleIcon fontSize="small" /></span>
                        <h1 className="user-title">User Details</h1>
                    </div>
                    <div className="user-header-actions">
                        <Link to="/UserIndex" className="usr-btn usr-btn-cancel">
                            <ArrowBackIcon style={{ fontSize: 14 }} />&nbsp;Back
                        </Link>
                    </div>
                </div>
            </div>

            {errorMessage && <div className="user-alert user-alert-error">{errorMessage}</div>}
            {successMessage && <div className="user-alert user-alert-success">{successMessage}</div>}

            <div className="user-form-card">
                <p className="user-form-section-title">User Information</p>
                <div className="user-form-row">
                    <div className="user-form-group">
                        <label className="user-label">First Name</label>
                        <input className="user-input" type="text" value={formData.FirstName} readOnly />
                    </div>
                    <div className="user-form-group">
                        <label className="user-label">Last Name</label>
                        <input className="user-input" type="text" value={formData.LastName} readOnly />
                    </div>
                    <div className="user-form-group">
                        <label className="user-label">User Name</label>
                        <input className="user-input" type="text" value={formData.UserName} readOnly />
                    </div>
                    <div className="user-form-group">
                        <label className="user-label">Contact Number</label>
                        <input className="user-input" type="text" value={formData.ContactNumber} readOnly />
                    </div>
                    <div className="user-form-group">
                        <label className="user-label">Email</label>
                        <input className="user-input" type="text" value={formData.Email} readOnly />
                    </div>
                </div>
            </div>

            <div className="user-detail-panels">
                {/* Claims Panel */}
                <div className="user-panel-card">
                    <div className="user-panel-header">
                        <span className="user-panel-title">User Claims</span>
                        {hasEditAdministrationPermission && (
                            isClaimEditMode
                                ? <button className="usr-btn usr-btn-create" onClick={saveClaims} disabled={isSavingClaims}>
                                    <SaveIcon style={{ fontSize: 13 }} />&nbsp;{isSavingClaims ? 'Saving...' : 'Save Claims'}
                                  </button>
                                : <button className="usr-btn usr-btn-create" onClick={() => setIsClaimEditMode(true)}>
                                    Manage Claims
                                  </button>
                        )}
                    </div>
                    <div className="user-panel-body">
                        <Select
                            options={[
                                { value: '1', label: 'Master' },
                                { value: '2', label: 'Transaction' },
                                { value: '3', label: 'Reports' },
                                { value: '4', label: 'Administration' },
                            ]}
                            value={selectedOption}
                            onChange={setSelectedOption}
                        />
                        <div className="user-claims-list">
                            {formDataClaim.Claim && formDataClaim.Claim.length > 0 ? (
                                formDataClaim.Claim.map((claim, index) => {
                                    const { startIndex, endIndex } = getClaimRange();
                                    if (index < startIndex || index > endIndex) return null;
                                    return (
                                        <div key={index} className="user-claim-item">
                                            <input type="hidden" name={`Claims[${index}].ClaimType`} value={claim.ClaimType} />
                                            <input type="hidden" name={`Claims[${index}].ClaimValue`} value={claim.ClaimValue} />
                                            <input type="hidden" name={`Claims[${index}].LogInClaims`} value={claim.LogInClaims} />
                                            <input
                                                type="checkbox"
                                                id={`claimCheckbox${index}`}
                                                className="user-checkbox"
                                                checked={claim.IsSelected}
                                                onChange={() => handleChangeClaim(index)}
                                                disabled={!isClaimEditMode}
                                            />
                                            <label htmlFor={`claimCheckbox${index}`} className="user-checkbox-label">{claim.ClaimValue}</label>
                                        </div>
                                    );
                                })
                            ) : <p className="user-empty-text">No claims available.</p>}
                        </div>
                    </div>
                </div>

                {/* Roles Panel */}
                <div className="user-panel-card">
                    <div className="user-panel-header">
                        <span className="user-panel-title">User Role</span>
                        {hasEditAdministrationPermission && (
                            isEditMode
                                ? <button className="usr-btn usr-btn-create" onClick={saveRoles} disabled={isSavingRoles}>
                                    <SaveIcon style={{ fontSize: 13 }} />&nbsp;{isSavingRoles ? 'Saving...' : 'Save Roles'}
                                  </button>
                                : <button className="usr-btn usr-btn-create" onClick={() => setIsEditMode(true)}>
                                    Manage Roles
                                  </button>
                        )}
                    </div>
                    <div className="user-panel-body">
                        <div className="user-claims-list">
                            {formData.Roles && formData.Roles.length > 0 ? (
                                formData.Roles.map((role, index) => (
                                    <div key={index} className="user-claim-item">
                                        <input type="hidden" name={`Roles[${index}].RoleName`} value={role.RoleName} />
                                        <input type="hidden" name={`Roles[${index}].RoleId`} value={role.RoleId} />
                                        <input
                                            type="checkbox"
                                            id={`roleCheckbox${index}`}
                                            className="user-checkbox"
                                            checked={role.IsSelected}
                                            onChange={() => handleChangeRole(index)}
                                            disabled={!isEditMode}
                                        />
                                        <label htmlFor={`roleCheckbox${index}`} className="user-checkbox-label">{role.RoleName}</label>
                                    </div>
                                ))
                            ) : <p className="user-empty-text">No roles available.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetail;
